/**
 * Order Finalization Service
 * 
 * Handles moving orders from draft (StoreOrders) to finalized (OrderHistory)
 * This implements the new database structure where:
 * - StoreOrders: Contains draft orders being worked on
 * - OrderHistory: Contains finalized/submitted orders
 */

import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  Timestamp,
  getFirestore
} from 'firebase/firestore';

export interface OrderData {
  orderId?: string;
  status?: string;
  isDraft?: boolean;
  draftSavedAt?: Date | Timestamp;
  finalizedAt?: Date | Timestamp;
  finalizedBy?: string;
  originalDraftId?: string;
  [key: string]: any;
}

export class OrderFinalizationService {
  private db = getFirestore();

  /**
   * Finalize an order - moves from StoreOrders to OrderHistory
   * @param storeId - The ID of the store
   * @param draftOrderId - The ID of the draft order to finalize
   * @param userId - The ID of the user finalizing the order
   * @returns Success status and final order ID or error message
   */
  async finalizeOrder(
    storeId: string,
    draftOrderId: string,
    userId: string
  ): Promise<{ success: boolean; finalOrderId?: string; error?: string }> {
    try {
      console.log(`🔄 Finalizing order ${draftOrderId} for store ${storeId}...`);

      // Step 1: Get the draft order
      const draftRef = doc(this.db, 'Stores', storeId, 'StoreOrders', draftOrderId);
      const draftSnap = await getDoc(draftRef);

      if (!draftSnap.exists()) {
        console.error('❌ Draft order not found');
        return { success: false, error: 'Draft order not found' };
      }

      const draftData = draftSnap.data() as OrderData;
      console.log('✅ Draft order retrieved');

      // Generate timestamp-based orderId (always use timestamp format for orderId)
      const now = new Date();
      const timestampId = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      
      // orderId is always the timestamp format (YYYYMMDDHHMM)
      // PO number is stored separately in poNumber field
      const orderIdentifier = timestampId;

      // Step 2: Create finalized order data
      const finalizedOrderData: OrderData = {
        ...draftData,
        orderId: orderIdentifier, // Always set orderId to timestamp format (YYYYMMDDHHMM)
        status: 'pending', // Changed from 'submitted' to 'pending' per requirements
        finalizedAt: Timestamp.now(),
        finalizedBy: userId,
        originalDraftId: draftOrderId,
        // Remove draft-specific fields
        isDraft: false,
        draftSavedAt: undefined
      };

      // Remove undefined values
      Object.keys(finalizedOrderData).forEach(key => {
        if (finalizedOrderData[key] === undefined) {
          delete finalizedOrderData[key];
        }
      });

      console.log('📦 Prepared finalized order data');

      // Step 3: Save to OrderHistory using orderIdentifier as document ID
      // This ensures the document ID matches the orderId field (timestamp or PO number)
      const historyCollectionRef = collection(this.db, 'Stores', storeId, 'OrderHistory');
      const historyRef = doc(historyCollectionRef, orderIdentifier);
      await setDoc(historyRef, finalizedOrderData);

      console.log(`✅ Order saved to history: ${historyRef.id}`);

      // Step 4: Delete ALL drafts from StoreOrders (per store, only ONE order should be in draft)
      // This ensures that StoreOrders is completely emptied after finalization
      const ordersRef = collection(this.db, 'Stores', storeId, 'StoreOrders');
      const allDraftsSnapshot = await getDocs(ordersRef);
      
      // Delete all drafts, not just the one being finalized
      const deletePromises = allDraftsSnapshot.docs.map(draftDoc => {
        return deleteDoc(doc(this.db, 'Stores', storeId, 'StoreOrders', draftDoc.id));
      });
      
      await Promise.all(deletePromises);
      console.log(`✅ All ${allDraftsSnapshot.docs.length} draft(s) deleted from StoreOrders`);

      // Trigger notification for order submitted
      try {
        const { NotificationTriggerService } = await import('./NotificationTriggerService');
        const store = await getDoc(doc(this.db, 'Stores', storeId));
        const storeName = store.exists() ? (store.data().StoreName || store.data().name) : undefined;
        
        NotificationTriggerService.triggerOrderSubmittedNotification(
          storeId,
          historyRef.id,
          storeName
        ).catch(err => {
          console.error('Failed to trigger order notification:', err);
          // Don't fail order finalization if notification fails
        });
      } catch (error) {
        console.error('Error importing notification service:', error);
        // Don't fail order finalization if notification fails
      }

      return {
        success: true,
        finalOrderId: historyRef.id
      };

    } catch (error: any) {
      console.error('❌ Error finalizing order:', error);
      return {
        success: false,
        error: error.message || 'Failed to finalize order'
      };
    }
  }

  /**
   * Get order history for a store
   * @param storeId - The ID of the store
   * @returns Array of finalized orders
   */
  async getOrderHistory(storeId: string): Promise<OrderData[]> {
    try {
      const historyRef = collection(this.db, 'Stores', storeId, 'OrderHistory');
      const snapshot = await getDocs(historyRef);

      return snapshot.docs.map(doc => ({
        orderId: doc.id,
        ...doc.data()
      } as OrderData));

    } catch (error) {
      console.error('Error fetching order history:', error);
      return [];
    }
  }

  /**
   * Get a specific finalized order
   * @param storeId - The ID of the store
   * @param orderId - The ID of the finalized order
   * @returns The order data or null if not found
   */
  async getFinalizedOrder(storeId: string, orderId: string): Promise<OrderData | null> {
    try {
      const orderRef = doc(this.db, 'Stores', storeId, 'OrderHistory', orderId);
      const orderSnap = await getDoc(orderRef);

      if (!orderSnap.exists()) {
        return null;
      }

      return {
        orderId: orderSnap.id,
        ...orderSnap.data()
      } as OrderData;

    } catch (error) {
      console.error('Error fetching finalized order:', error);
      return null;
    }
  }

  /**
   * Check if there's an active draft for a store
   * @param storeId - The ID of the store
   * @returns The draft order or null if none exists
   */
  async getActiveDraft(storeId: string): Promise<OrderData | null> {
    try {
      const draftsRef = collection(this.db, 'Stores', storeId, 'StoreOrders');
      const snapshot = await getDocs(draftsRef);

      if (snapshot.empty) {
        return null;
      }

      // Return the first draft (should only be one active draft per store)
      const draftDoc = snapshot.docs[0];
      return {
        orderId: draftDoc.id,
        ...draftDoc.data()
      } as OrderData;

    } catch (error) {
      console.error('Error fetching active draft:', error);
      return null;
    }
  }

  /**
   * Get count of finalized orders for a store
   * @param storeId - The ID of the store
   * @returns The count of finalized orders
   */
  async getOrderHistoryCount(storeId: string): Promise<number> {
    try {
      const historyRef = collection(this.db, 'Stores', storeId, 'OrderHistory');
      const snapshot = await getDocs(historyRef);
      return snapshot.size;
    } catch (error) {
      console.error('Error counting order history:', error);
      return 0;
    }
  }

  /**
   * Check if a store has any draft orders
   * @param storeId - The ID of the store
   * @returns True if there's at least one draft
   */
  async hasDraftOrder(storeId: string): Promise<boolean> {
    try {
      const draftsRef = collection(this.db, 'Stores', storeId, 'StoreOrders');
      const snapshot = await getDocs(draftsRef);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking for draft orders:', error);
      return false;
    }
  }
}

